import { useEffect, useRef, useCallback } from 'react';
import { useTour } from '../../../context/TourContext';

const DEFAULT_POPOVER_WIDTH = 340;
const ARROW_SIZE = 10;
const GAP = 16; // gap between target and popover

export default function TourPopover() {
  const {
    currentStep,
    currentStepIndex,
    totalSteps,
    targetRect,
    targetEl,
    popoverRect,
    stepData,
    hideBack,
    defaultAlign,
    popoverWidth,
    nextStep,
    prevStep,
    endTour,
  } = useTour();

  const POPOVER_WIDTH = popoverWidth || DEFAULT_POPOVER_WIDTH;

  // Step body and title can be strings or functions that receive the
  // per-tour stepData bag (passed when startTour was called).
  const resolveField = (field) => (typeof field === 'function' ? field(stepData || {}) : field);
  const resolvedTitle = resolveField(currentStep?.title);
  const resolvedBody = resolveField(currentStep?.body);

  const popoverRef = useRef(null);

  // Prefer the popover-specific anchor rect (if the step overrode it);
  // fall back to the highlight target otherwise.
  const positionRect = popoverRect || targetRect;

  // Calculate position relative to the target
  const getPosition = useCallback(() => {
    if (!positionRect || !popoverRef.current) return {};

    const popoverH = popoverRef.current.offsetHeight;
    const placement = currentStep?.placement || 'bottom';
    // Cross-axis alignment for left/right placements. Step-level override
    // wins; otherwise fall back to the tour's defaultAlign (e.g. 'start'
    // for Classification/Normal Balance reflection tours, which top-align
    // the popover with the column header).
    const align = currentStep?.align || defaultAlign || 'center';
    const viewportW = window.innerWidth;
    const viewportH = window.innerHeight;

    let top, left, actualPlacement;

    switch (placement) {
      case 'center':
        // Two flavours of "center":
        //  - Real positional anchor (target rect or popoverAnchorId, even
        //    on noOverlay steps) → horizontally centered, top-aligned with
        //    the anchor's top edge so the popover sits level with the
        //    column header instead of floating mid-screen.
        //  - No anchor → viewport center, lifted 40px so the 64px header
        //    doesn't make it read low.
        if (positionRect && positionRect.height > 0) {
          top = positionRect.top;
        } else {
          top = (viewportH - popoverH) / 2 - 40;
        }
        left = (viewportW - POPOVER_WIDTH) / 2;
        actualPlacement = 'center';
        break;
      case 'top':
        top = positionRect.top - popoverH - GAP - ARROW_SIZE;
        left = positionRect.left + positionRect.width / 2 - POPOVER_WIDTH / 2;
        actualPlacement = top < 0 ? 'bottom' : 'top';
        if (actualPlacement === 'bottom') {
          top = positionRect.top + positionRect.height + GAP + ARROW_SIZE;
        }
        break;
      case 'left':
        top = align === 'start'
          ? positionRect.top
          : positionRect.top + positionRect.height / 2 - popoverH / 2;
        left = positionRect.left - POPOVER_WIDTH - GAP - ARROW_SIZE;
        actualPlacement = left < 0 ? 'right' : 'left';
        if (actualPlacement === 'right') {
          left = positionRect.left + positionRect.width + GAP + ARROW_SIZE;
        }
        break;
      case 'right':
        top = align === 'start'
          ? positionRect.top
          : positionRect.top + positionRect.height / 2 - popoverH / 2;
        left = positionRect.left + positionRect.width + GAP + ARROW_SIZE;
        actualPlacement = (left + POPOVER_WIDTH > viewportW) ? 'left' : 'right';
        if (actualPlacement === 'left') {
          left = positionRect.left - POPOVER_WIDTH - GAP - ARROW_SIZE;
        }
        break;
      case 'bottom':
      default:
        top = positionRect.top + positionRect.height + GAP + ARROW_SIZE;
        left = positionRect.left + positionRect.width / 2 - POPOVER_WIDTH / 2;
        actualPlacement = (top + popoverH > viewportH) ? 'top' : 'bottom';
        if (actualPlacement === 'top') {
          top = positionRect.top - popoverH - GAP - ARROW_SIZE;
        }
        break;
    }

    // Clamp horizontal
    if (left < 12) left = 12;
    if (left + POPOVER_WIDTH > viewportW - 12) left = viewportW - 12 - POPOVER_WIDTH;

    // Clamp vertical
    if (top < 12) top = 12;
    if (top + popoverH > viewportH - 12) top = viewportH - 12 - popoverH;

    return { top, left, actualPlacement: actualPlacement || placement };
  }, [positionRect, currentStep, POPOVER_WIDTH, defaultAlign]);

  // Handle click on target element for 'click' advance steps
  useEffect(() => {
    if (!currentStep || currentStep.advanceOn !== 'click' || !targetEl) return;

    const clickSelector = currentStep.clickTarget;
    const clickEl = clickSelector ? document.querySelector(clickSelector) : targetEl;
    if (!clickEl) return;

    const handler = () => {
      // Delay to let the click's side effects (API calls, navigation) complete
      setTimeout(() => nextStep(), 1000);
    };

    clickEl.addEventListener('click', handler);
    return () => clickEl.removeEventListener('click', handler);
  }, [currentStep, targetEl, nextStep]);

  // Handle click on target element for 'close' steps — end the tour
  useEffect(() => {
    if (!currentStep || currentStep.advanceOn !== 'close' || !targetEl) return;

    const handler = () => {
      endTour();
    };

    targetEl.addEventListener('click', handler);
    return () => targetEl.removeEventListener('click', handler);
  }, [currentStep, targetEl, endTour]);

  // Focus trap & keyboard
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        endTour();
      }
      if (e.key === 'Enter' && currentStep?.advanceOn === 'next') {
        nextStep();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [endTour, nextStep, currentStep]);

  // Focus the popover on mount / step change
  useEffect(() => {
    if (popoverRef.current) {
      popoverRef.current.focus();
    }
  }, [currentStepIndex]);

  const pos = getPosition();

  // Arrow positioning
  const getArrowStyle = () => {
    if (!pos.actualPlacement) return {};
    switch (pos.actualPlacement) {
      case 'bottom':
        return {
          top: -ARROW_SIZE,
          left: '50%',
          transform: 'translateX(-50%) rotate(45deg)',
        };
      case 'top':
        return {
          bottom: -ARROW_SIZE,
          left: '50%',
          transform: 'translateX(-50%) rotate(45deg)',
        };
      case 'left':
        return {
          right: -ARROW_SIZE,
          top: '50%',
          transform: 'translateY(-50%) rotate(45deg)',
        };
      case 'right':
        return {
          left: -ARROW_SIZE,
          top: '50%',
          transform: 'translateY(-50%) rotate(45deg)',
        };
      default:
        return {};
    }
  };

  const progressPercent = ((currentStepIndex + 1) / totalSteps) * 100;

  return (
    <div
      ref={popoverRef}
      className="tour-popover"
      role="dialog"
      aria-labelledby="tour-popover-title"
      aria-describedby="tour-popover-body"
      tabIndex={-1}
      style={{
        top: pos.top ?? 0,
        left: pos.left ?? 0,
        width: POPOVER_WIDTH,
      }}
    >
      {pos.actualPlacement !== 'center' && (
        <div className="tour-popover-arrow" style={getArrowStyle()} />
      )}

      <div className="tour-popover-progress-bar">
        <div
          className="tour-popover-progress-fill"
          style={{ width: `${progressPercent}%` }}
        />
      </div>

      <div className="tour-popover-step-number">
        Step {currentStepIndex + 1} of {totalSteps}
      </div>

      <h3 id="tour-popover-title" className="tour-popover-title">
        {resolvedTitle}
      </h3>

      {Array.isArray(resolvedBody) ? (
        <div id="tour-popover-body" className="tour-popover-body">
          {resolvedBody.map((paragraph, i) => {
            // Allow inline HTML (e.g. <b>) so step copy can emphasize
            // specific terms inside each paragraph.
            const isHTML = typeof paragraph === 'string' && /<\/?[a-z][\s\S]*>/i.test(paragraph);
            return isHTML ? (
              <p
                key={i}
                className="tour-popover-body-paragraph"
                dangerouslySetInnerHTML={{ __html: paragraph }}
              />
            ) : (
              <p key={i} className="tour-popover-body-paragraph">
                {paragraph}
              </p>
            );
          })}
        </div>
      ) : (
        <p id="tour-popover-body" className="tour-popover-body">
          {resolvedBody}
        </p>
      )}

      <div className="tour-popover-buttons">
        {currentStep?.advanceOn === 'close' ? (
          <button className="tour-btn tour-btn-next" onClick={endTour}>
            Close
          </button>
        ) : (
          <>
            {!hideBack && currentStepIndex > 0 && (
              <button className="tour-btn tour-btn-back" onClick={prevStep}>
                Back
              </button>
            )}

            {currentStep?.advanceOn === 'next' && (
              <button className="tour-btn tour-btn-next" onClick={nextStep}>
                {currentStepIndex === totalSteps - 1 ? 'Finish' : 'Next'}
              </button>
            )}

            <button className="tour-btn tour-btn-skip" onClick={endTour}>
              Skip tour
            </button>
          </>
        )}
      </div>
    </div>
  );
}
